import React from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from 'react-query';
import { Button, Modal } from 'antd';

import { queryKeys } from '../../queries/queryKeys';
import { texts } from '../../../../constants/texts';
import { FieldPropType } from '../../propTypes';
import { createAdminInfoField } from '../../requests/createAdminInfoField';
import { deleteAdminInfoField } from '../../requests/deleteAdminInfoField';
import showErrorMessage from '../../../../../../helpers/showErrorMessage';

export const AdminInfoActions = ({
  companyId,
  form,
  initialValues,

  isEditing,
  setIsEditing,

  isSortEditing,
  setIsSortEditing,

  setIsInfoSaving,

  cancelSort,
  cancelEdit,

  deleteFieldsQueue,
  removeDeleteFieldFromQueue,
}) => {
  const queryClient = useQueryClient();

  const startInfoSaving = () => setIsInfoSaving(true);
  const endInfoSaving = () => setIsInfoSaving(false);

  const saveCompanyInfoMutation = useMutation(
    async (values) => {
      const formValuesMap = new Map(
        Object.entries(values.formData).map(([id, data]) => [Number(id), data])
      );
      const changedValues = initialValues
        .filter((f) => deleteFieldsQueue.every((field) => field.id !== f.id))
        .filter((f) => {
          if (!formValuesMap.has(f.id)) {
            return false;
          }
          const changedFieldData = formValuesMap.get(f.id);
          const isChanged = ['value', 'orderNum'].some(
            (key) => f[key] !== changedFieldData[key]
          );
          return isChanged;
        })
        .map((f) => {
          const changedFieldData = formValuesMap.get(f.id);
          return {
            ...f,
            ...changedFieldData,
          };
        });

      if (!isSortEditing && deleteFieldsQueue.length) {
        await Promise.allSettled(
          deleteFieldsQueue.map((field) => {
            return deleteAdminInfoField({
              companyId,
              fieldId: field.id,
            })
              .then(() => removeDeleteFieldFromQueue(field))
              .catch((error) => {
                showErrorMessage(
                  error,
                  `Произошла ошибка при удалении поля: "${field.name}"`
                );
              });
          })
        );
      }

      if (!changedValues.length) {
        return;
      }

      return createAdminInfoField({
        companyId: values.companyId,
        requestBody: {
          Items: changedValues,
        },
      });
    },
    {
      onMutate: () => {
        startInfoSaving();
      },
      onSuccess: () => {
        if (isSortEditing) {
          setIsSortEditing(false);
        } else {
          setIsEditing(false);
        }
        endInfoSaving();
        queryClient.invalidateQueries(queryKeys.adminInfo(companyId));
      },
      onError: (error) => {
        endInfoSaving();
        showErrorMessage(error, 'Произошла ошибка при сохранении данных');
      },
    }
  );

  const handleEditingSave = () => {
    form.validateFieldsAndScroll((err, formValues) => {
      if (err) {
        return;
      }
      Modal.confirm({
        title: texts.saveEditConfirmation,
        okText: texts.yes,
        cancelText: texts.no,
        onOk: () => {
          saveCompanyInfoMutation.mutate({
            companyId: companyId,
            formData: formValues,
          });
        },
        onCancel: () => {},
      });
    });
  };

  const handleEditingCancel = () => {
    Modal.confirm({
      title: texts.cancelEditConfirmation,
      okText: texts.yes,
      cancelText: texts.no,
      onOk: () => {
        setIsEditing(false);
        cancelEdit();
      },
      onCancel: () => {},
    });
  };

  const handleSortEditingCancel = () => {
    cancelSort();
    setIsSortEditing(false);
  };

  const handleSave = () => {
    handleEditingSave();
  };

  const handleCancel = () => {
    if (isSortEditing) {
      handleSortEditingCancel();
    } else {
      handleEditingCancel();
    }
  };

  const activateSorting = () => {
    form.validateFieldsAndScroll((err, formValues) => {
      if (err) {
        Modal.error({
          title: 'Невозможно изменить порядок полей',
          content:
            'Прежде чем приступить к изменению порядка полей, исправьте ошибки в полях формы.',
        });
        return;
      }
      setIsSortEditing(true);
    });
  };

  return !isEditing ? (
    <Button
      type="link"
      onClick={() => setIsEditing(true)}
    >
      Редактировать
    </Button>
  ) : (
    <>
      {!isSortEditing && (
        <Button
          type="link"
          onClick={activateSorting}
          disabled={saveCompanyInfoMutation.isLoading}
        >
          Изменить порядок
        </Button>
      )}
      <Button
        type="link"
        onClick={handleCancel}
        disabled={saveCompanyInfoMutation.isLoading}
      >
        Отмена
      </Button>
      <Button
        type="link"
        onClick={handleSave}
        loading={saveCompanyInfoMutation.isLoading}
        disabled={saveCompanyInfoMutation.isLoading}
      >
        Сохранить
      </Button>
    </>
  );
};

AdminInfoActions.propTypes = {
  companyId: PropTypes.number.isRequired,
  form: PropTypes.object.isRequired,
  initialValues: PropTypes.arrayOf(FieldPropType).isRequired,

  isEditing: PropTypes.bool.isRequired,
  setIsEditing: PropTypes.func.isRequired,
  isSortEditing: PropTypes.bool.isRequired,
  setIsSortEditing: PropTypes.func.isRequired,
  setIsInfoSaving: PropTypes.func.isRequired,

  cancelSort: PropTypes.func.isRequired,
  cancelEdit: PropTypes.func.isRequired,

  deleteFieldsQueue: PropTypes.arrayOf(FieldPropType).isRequired,
  removeDeleteFieldFromQueue: PropTypes.func.isRequired,
};
